<template>
    <div class="purchase-item" :class="[{ 'is-dark-mode': dark }, getTierClass]">
        <div class="purchase-item-box">
            <div class="color-header">
                <div class="purchase-title">
                    {{ headerLabel || bundle.nickname || 'BUNDLE' }}
                </div>
            </div>
            <div class="purchase-content">
                <!-- Default: value/unit/buy button, for the bundle-picker grid.
                     PurchaseModal's pay step overrides this to put the mounted
                     Payment Element + Pay button in the same card shell instead,
                     so checkout looks like the same product card, not a
                     different UI bolted on next to it. -->
                <slot>
                    <div class="purchase-value">{{ bundle.quantity.toLocaleString() }}</div>
                    <div class="purchase-unit">{{ bundle.type === 'gold' ? 'GOLD' : 'CREDITS' }}</div>
                    <a @click="!buying && $emit('purchase', bundle)" class="purchase-button" :class="{ 'is-loading': buying }">{{ formattedPrice }}</a>
                </slot>
            </div>
        </div>
        <!-- The badge reads "how much" at a glance before the number below
             is even parsed. Tier 1 is just the one coin. Tier 2 is that
             same coin with two smaller ones peeking out from behind each
             side. Tier 3 is that same coin sitting in front of a whole
             scattered pile, clipped by the circle like you're looking down
             into it — coin-1 is always identical across all three and is
             deliberately last in the DOM so it's the one on top. -->
        <div class="circle-icon" :class="'is-tier-' + iconTier">
            <div class="circle-icon-holder">
                <template v-if="iconTier === 2">
                    <img class="coin coin-2" :src="icon" alt="" draggable="false">
                    <img class="coin coin-3" :src="icon" alt="" draggable="false">
                </template>
                <template v-else-if="iconTier >= 3">
                    <img
                        v-for="c in pileCoins" :key="c.id"
                        class="coin coin-bg" :src="icon" alt="" draggable="false"
                        :style="{
                            top: c.top + 'px',
                            left: c.left + 'px',
                            width: c.size + 'px',
                            height: c.size + 'px',
                            '--rot': c.rot + 'deg',
                            animationDuration: c.duration + 's',
                            animationDelay: c.delay + 's',
                        }">
                </template>
                <img class="coin coin-1" :src="icon" alt="" draggable="false">
            </div>
        </div>
    </div>
</template>
<script>
export default {
    name: 'PurchaseItem',
    props: {
        // A fixed-price bundle as returned by GET /api/checkout/prices:
        // { id, type: 'credits'|'gold', quantity, unitAmount, currency, nickname }
        bundle: {
            type: Object,
            required: true,
        },
        // Purely cosmetic ranking (1 = basic, 2 = default, 3+ = best value)
        // for which of the fixed bundles is being shown — not a Stripe field.
        tier: {
            type: Number,
            default: 2,
        },
        buying: Boolean,
        // Overrides the color-header text (defaults to the Stripe nickname,
        // e.g. "5000 Credits") — PurchaseModal uses this so the grid shows a
        // sales pitch ("Great value") instead of repeating the quantity
        // that's already shown below, but switches back to the literal
        // quantity once that quantity line is hidden during checkout.
        headerLabel: String,
    },
    computed: {
        getTierClass() {
            if      (this.tier <= 1) return { 'is-basic':   true }
            else if (this.tier <= 2) return { 'is-primary': true }
            else                     return { 'is-special': true }
        },
        // Clamped to 1-3 for the coin badge — tier itself is only ever
        // 1/2/3 in practice (see PurchaseModal's tierFor()), but this keeps
        // the badge from silently rendering nothing if that ever changes.
        iconTier() {
            return Math.min(Math.max(this.tier, 1), 3)
        },
        dark() {
            return this.bundle.type === 'gold'
        },
        formattedPrice() {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: this.bundle.currency || 'usd',
            }).format(this.bundle.unitAmount / 100)
        },
        icon() {
            return this.dark
                ? require('@/assets/images/currency/128w/gold.png')
                : require('@/assets/images/currency/128w/credits.png')
        },
        // The tier-3 badge's background pile. top/left/size/rot lay each
        // coin out (in px, within the 72px circle — see the .coin comment
        // in <style>); duration/delay drive the rain animation and are
        // deliberately all different from each other (not just tied to
        // size) so the pile doesn't visually resolve into "a few groups
        // moving together" — every coin falls at its own distinct pace.
        pileCoins() {
            return [
                { id: 1,  top: -8,  left: -8,  size: 31, rot: 25,  duration: 6.5,  delay: 0 },
                { id: 2,  top: -8,  left: 44,  size: 30, rot: -20, duration: 9,    delay: -2 },
                { id: 3,  top: 44,  left: -8,  size: 30, rot: -15, duration: 13,   delay: -5 },
                { id: 4,  top: 44,  left: 44,  size: 29, rot: 18,  duration: 7.5,  delay: -1 },
                { id: 5,  top: -10, left: 18,  size: 28, rot: 8,   duration: 16,   delay: -9 },
                { id: 6,  top: 50,  left: 18,  size: 28, rot: -10, duration: 10.5, delay: -3 },
                { id: 7,  top: 18,  left: -12, size: 27, rot: 30,  duration: 19,   delay: -7 },
                { id: 8,  top: 18,  left: 56,  size: 27, rot: -28, duration: 8,    delay: -4 },
                { id: 9,  top: 6,   left: 6,   size: 26, rot: -35, duration: 14.5, delay: -6 },
                { id: 10, top: 38,  left: 38,  size: 26, rot: 22,  duration: 11,   delay: -8 },
                { id: 11, top: -10, left: 10,  size: 25, rot: -40, duration: 17.5, delay: -10 },
                { id: 12, top: 50,  left: 44,  size: 25, rot: 15,  duration: 12.5, delay: -1 },
                { id: 13, top: 16,  left: 30,  size: 24, rot: 5,   duration: 20,   delay: -13 },
                { id: 14, top: 30,  left: 2,   size: 24, rot: -8,  duration: 7,    delay: -2 },
                { id: 15, top: 4,   left: 52,  size: 26, rot: 33,  duration: 15,   delay: -5 },
                { id: 16, top: 52,  left: 4,   size: 25, rot: -18, duration: 9.5,  delay: -6 },
            ]
        },
    },
}
</script>

<style lang="scss">
.purchase-item {
    position: relative;
    font-weight: bold;
    .circle-icon {
        border-radius: 100%;
        width: 4.5rem;
        height: 4.5rem;
        margin: 0 auto;
        text-align: center;
        background-color: white;
        box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.15);
        position: absolute;
        left: calc(50% - 2.25rem);
        top: -34px;
        overflow: hidden;
        // Belt-and-suspenders with overflow:hidden above, but clip-path is
        // what actually does the clipping for the rain animation's coins —
        // Safari promotes animated `transform` children to their own
        // compositing layer, which lets them escape an ancestor's
        // overflow:hidden + border-radius clip (their drop-shadow filter
        // was bleeding past the circle's edge). clip-path isn't subject to
        // that same escape.
        clip-path: circle(50%);
        .circle-icon-holder {
            width: 100%;
            height: 100%;
            position: relative;
        }
        // Coordinates below are tuned against the 72px (4.5rem) circle above
        // — they're not proportional units because at this size "coins
        // offset by a few px" only reads as a deliberate fan/stack at an
        // exact scale, not a percentage of it.
        .coin {
            position: absolute;
            object-fit: contain;
            filter: drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.25));
        }
        // The front coin — identical across all three tiers, on purpose.
        .coin-1 {
            top: 13px;
            left: 13px;
            width: 46px;
            height: 46px;
        }
        // Two "buddy" coins tucked in close behind the front coin, just
        // peeking out past its left/right edge rather than sitting way out
        // on their own.
        &.is-tier-2 {
            .coin-2, .coin-3 {
                width: 34px;
                height: 34px;
                top: 19px;
            }
            .coin-2 {
                left: 0px;
                transform: rotate(-14deg);
            }
            .coin-3 {
                left: 38px;
                transform: rotate(14deg);
            }
        }
        // A dense, crooked pile behind the front coin that fills the whole
        // badge edge-to-edge — several sit partly outside the 72px box on
        // purpose (see pileCoins() in <script>), so .circle-icon's
        // overflow:hidden crops them at the circle's edge instead of
        // leaving bare background showing through the corners, like the
        // badge is a clipped view down into a much bigger pile.
        //
        // Every coin shares this one @keyframes — rotation comes in via
        // the --rot custom property each gets inline, so one rule covers
        // any number of coins instead of needing a rule+keyframes pair
        // per coin. translateY runs from -90px to 90px, comfortably
        // outside the 72px circle at both ends, so the loop point (jumping
        // back from +90 to -90) happens while invisible — that reads as an
        // unbroken fall instead of a visible snap. top/left (set inline
        // per coin) is only the position each one drifts through at the
        // animation's midpoint, not a resting point. duration/delay are
        // also set inline per coin, individually — not tied to size — so
        // the pile doesn't read as a few clusters moving in lockstep.
        &.is-tier-3 .coin-bg {
            animation-name: coin-rain;
            animation-timing-function: linear;
            animation-iteration-count: infinite;
            // Falling doesn't stop cleanly at a random point for anyone
            // sensitive to motion — hold each coin at its midpoint pose.
            @media (prefers-reduced-motion) {
                animation: none !important;
                transform: rotate(var(--rot));
            }
        }
    }

    @keyframes coin-rain {
        0%   { transform: translateY(-90px) rotate(var(--rot)); }
        100% { transform: translateY(90px) rotate(var(--rot)); }
    }
    .purchase-item-box {
        margin-top: 30px;
        border-radius: 10px;
        background-color: white;
        overflow: hidden;
        box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.15);
        border-radius: 9px;
        // Lift on hover so the cards read as interactive/pickable rather
        // than static price tags sitting on the page.
        transition: transform 0.2s ease, box-shadow 0.2s ease;
        .color-header {
            position: relative;
            padding: 0.5rem;
            padding-top: 2.5rem;
            color: white;
            // A deeper teal than the old #52D9E9 — that flat light-cyan
            // fill was a WCAG contrast failure with white text (~1.9:1).
            // This is a plain background-color (not a gradient) on purpose:
            // PurchaseModal.vue's gold-mode override only reassigns
            // background-color for this tier, so a gradient here would
            // still show through underneath it in gold mode.
            background-color: #0F6E80;
            overflow: hidden;
            // A soft diagonal sheen instead of a flat color fill — gives
            // the header some depth without touching contrast, since it's
            // just a highlight layered on top.
            &::after {
                content: '';
                position: absolute;
                inset: 0;
                background: linear-gradient(135deg, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0) 55%);
                pointer-events: none;
            }
            .purchase-title {
                position: relative;
                z-index: 1;
            }
        }
        .purchase-content {
            text-align: center;
            padding: 10px;
            .purchase-value {
                font-size: 2.5rem;
                color: #646978;
            }
            .purchase-unit {
                color: #A9ADBA;
                margin-bottom: 1rem;
            }
            .purchase-button {
                // Buefy/Bulma wrap the label in its own <span> with its own
                // line-height — display:block left vertical centering to
                // that line-height instead of the button's actual content
                // box, which visibly pushed the text off-center. Flexbox
                // centering doesn't care how the label's marked up.
                display: flex;
                align-items: center;
                justify-content: center;
                // Same fix and same reasoning as .color-header above: a
                // plain background-color (not a gradient) so gold mode's
                // background-color-only override still fully replaces it.
                background-color: #0F6E80;
                padding: 14px 0;
                min-height: 3.25rem;
                font-size: 1.1rem;
                width: 100%;
                color: white;
                font-weight: bold;
                border-radius: 8px;
                box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.15);
                transition: all 0.2s;
                &:hover:not(.is-loading) {
                    filter: brightness(1.05);
                }
                &.is-loading {
                    cursor: wait;
                    opacity: 0.7;
                }
                // Buefy's b-button already sets the disabled attribute, but
                // without this it's still visually identical to the enabled
                // state — background/shadow don't care about :disabled on
                // their own, so a locked Pay button still looked clickable.
                &:disabled, &.is-disabled {
                    cursor: not-allowed;
                    filter: grayscale(0.6);
                    opacity: 0.5;
                    box-shadow: none;
                    &:hover { filter: grayscale(0.6); }
                }
            }
        }
    }
    &.is-basic {
        // Kept as a plain background-color (not a gradient) — same reason
        // as the default tier above: PurchaseModal.vue's gold-mode
        // override for this tier only reassigns background-color.
        .color-header {
            background-color: #424242;
        }
        .purchase-content .purchase-button {
            background-color: #424242;
        }
    }
    &.is-special {
        .color-header {
            background-color: unset;
            background: linear-gradient(101.67deg, #E952D1 0%, #52D9E9 100%);
        }
        .purchase-content .purchase-button {
            background-color: unset;
            background: linear-gradient(101.67deg, #E952D1 0%, #52D9E9 100%);
        }
    }
    // Cards read as flat/static without this — lifting the whole box (not
    // just the button) on hover makes them feel like pickable tiles.
    &:hover .purchase-item-box {
        transform: translateY(-4px);
        box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.18);
    }
}
</style>
